import React from "react";
import { TopBar } from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import {
  apiBoronganTarif, apiBoronganRekap, apiBoronganEntri, apiBoronganSaya,
  apiBoronganPenyesuaian, apiBoronganHitungUlang, apiBoronganKunci, apiBoronganBayar, apiBoronganCek, apiBukuKas,
  apiBoronganSelesaikan, apiListUsers, formatApiErrorDetail,
} from "@/lib/api";
import { rupiah, tanggalIndo, geserMinggu } from "@/lib/format";
import { bisaTandaiSelesai, labelPeriode, teksDiselesaikan } from "@/lib/borongan";
import { ChevronLeft, ChevronRight, Lock, Wallet, Loader2, Printer, AlertTriangle, RefreshCw } from "lucide-react";
import * as W from "@/lib/waktu";
import TarifJahitPanel from "@/components/TarifJahitPanel";

// Gaji borongan (F1.5-K). Owner & Finance: weekly recap (Mon–Sun), lock after
// the week ended, pay per operator → automatic cash entry. Owner: rates and
// adjustments. Operators: only their own entries. All values come from the
// server; this page never computes pay.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "max-w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const lbl = "block text-[13px] font-semibold text-slate-200";
const JENIS_CLS = { HASIL: "text-emerald-300", SUSULAN: "text-emerald-300", POTONGAN: "text-red-300", PENYESUAIAN: "text-amber-300" };

// v2.24.1: the week's first day comes from the setting (server answer carries the day names)
function MingguNav({ minggu, sampai, d, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <button data-testid="borongan-minggu-prev" onClick={() => onChange(geserMinggu(minggu, -1))} className="rounded border border-slate-700 p-1.5 text-slate-300"><ChevronLeft size={16} /></button>
      <span data-testid="borongan-minggu" className="font-mono text-sm text-slate-200">{labelPeriode({ ...d, minggu, sampai }, tanggalIndo)}</span>
      <button data-testid="borongan-minggu-next" onClick={() => onChange(geserMinggu(minggu, 1))} className="rounded border border-slate-700 p-1.5 text-slate-300"><ChevronRight size={16} /></button>
    </div>
  );
}

// v2.24.3: owner marks an unpriced entry (paid by a Penyesuaian) as settled — reason required, server writes SUSULAN Rp0
function TandaiSelesai({ entri, onDone }) {
  const [buka, setBuka] = React.useState(false);
  const [alasan, setAlasan] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const kirim = async () => {
    setBusy(true); setMsg(null);
    try { await apiBoronganSelesaikan(entri.id, alasan); setBuka(false); setAlasan(""); onDone(); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  if (!buka) return <button data-testid={`borongan-selesai-${entri.id}`} onClick={() => setBuka(true)} className="mt-1 rounded border border-amber-500/50 px-2 py-0.5 text-[11px] text-amber-300">Tandai diselesaikan lewat Penyesuaian</button>;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      <input data-testid="borongan-selesai-alasan" value={alasan} minLength={5} maxLength={300} placeholder="Alasan (mis. dibayar lewat Penyesuaian Rp22.000, 4 Nov)" onChange={(e) => setAlasan(e.target.value)} className={`${inputCls} w-72 max-w-full py-1 text-xs`} />
      <button data-testid="borongan-selesai-simpan" disabled={busy || alasan.trim().length < 5} onClick={kirim} className="rounded bg-amber-500 px-2 py-1 text-[11px] font-semibold text-slate-950 disabled:opacity-40">Simpan</button>
      <button onClick={() => setBuka(false)} className="text-[11px] text-slate-400">Batal</button>
      <span className="w-full text-[11px] text-slate-500">Nilai tetap Rp0 (tidak dibayar dua kali); minggu bisa dikunci setelah semua catatan tanpa tarif ditandai.</span>
      {msg && <span className="w-full text-[11px] text-red-300">{msg}</span>}
    </div>
  );
}

function DaftarEntri({ rows, isAdmin = false, terkunci = true, onDone }) {
  if (!rows) return <Loader2 size={16} className="animate-spin text-slate-400" />;
  if (!rows.length) return <p className="text-sm text-slate-500">Belum ada catatan minggu ini.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs font-semibold text-slate-500">
          <th className="px-2 py-1">Tanggal</th><th className="px-2 py-1">Jenis</th><th className="px-2 py-1">Pos / SPK</th>
          <th className="px-2 py-1">Rincian</th><th className="px-2 py-1 text-right">Nilai</th></tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id} className="border-t border-slate-800 align-top">
              <td className="px-2 py-1.5 text-xs">{tanggalIndo(e.tanggal)}</td>
              <td className={`px-2 py-1.5 text-xs font-mono ${JENIS_CLS[e.jenis]}`}>{e.jenis}
                {e.rework_ke ? <span data-testid={`borongan-rework-${e.id}`} className={`block font-sans ${e.dibayar ? "text-emerald-300" : "text-red-300"}`}>rework ke-{e.rework_ke} · {e.dibayar ? "dibayar" : "tidak dibayar"}</span> : null}</td>
              <td className="px-2 py-1.5 text-xs">{e.pos_nama || e.pos_code || "—"}<span className="block text-slate-500">{e.nomor_spk}</span></td>
              <td className="px-2 py-1.5 text-xs text-slate-400">
                {e.rincian?.map((x, i) => (
                  <div key={i}>{x.nama}: {x.satuan === "PER_M2" ? `${(x.basis / 1000).toLocaleString("id-ID")} m²` : `${x.basis}`} × {rupiah(x.nominal)}{!x.tarif_id && <span className="text-amber-300"> TARIF BELUM ADA{x.belum?.length ? ` (${x.belum.join(", ")})` : ""}</span>}
                    {x.komponen?.length > 0 && x.tarif_id && <span className="block text-slate-500">{x.komponen.map((k) => `${k.nama} ${rupiah(k.nominal)}`).join(" + ")}</span>}</div>
                ))}
                {e.keterangan && <div>{e.keterangan}</div>}
                {e.perlu_ditinjau && <div className="text-amber-300">perlu ditinjau Owner</div>}
                {e.diselesaikan && <div data-testid={`borongan-diselesaikan-${e.id}`} className="text-emerald-300">{teksDiselesaikan(e, W.waktu)}</div>}
                {bisaTandaiSelesai(e, isAdmin, terkunci) && <TandaiSelesai entri={e} onDone={onDone} />}
              </td>
              <td className={`px-2 py-1.5 text-right font-mono ${e.nilai < 0 ? "text-red-300" : ""}`}>{rupiah(e.nilai)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Saya() {
  const [minggu, setMinggu] = React.useState(null);
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => { apiBoronganSaya(minggu).then((x) => { setD(x); setError(null); }).catch((e) => setError(errText(e))); }, [minggu]);
  return (
    <div className="space-y-3">
      {d && <MingguNav minggu={d.minggu} sampai={d.sampai} d={d} onChange={setMinggu} />}
      {error && <div className="text-sm text-red-300">{error}</div>}
      {d && <div data-testid="borongan-saya-total" className="text-lg">Total minggu ini: <b className="font-mono">{rupiah(d.total)}</b></div>}
      <DaftarEntri rows={d?.entri} />
      <p className="text-xs text-slate-500">Nilai bisa berubah sampai minggu dikunci Owner/Finance (mis. tarif baru diisi atau potongan reject).</p>
    </div>
  );
}

// Addendum 2: one rate per finished piece for Cutting & Jahit (same for every
// product); a change is a new rate from its start date, history kept.
// TAMPILAN-SOQ tahap 2 (owner 2026-10-10): the single rate of Addendum 2 is an
// archive — read only. Its data stay: SPKs made before the per-sewing-type
// rates (no jahit_rencana) still pay from it. New rates: tab "Tarif per jenis
// jahitan". (The Owner-only API is unchanged.)
function Tarif() {
  const [d, setD] = React.useState(null);
  const [msg, setMsg] = React.useState(null);
  const load = () => apiBoronganTarif().then(setD).catch((e) => setMsg(errText(e)));
  React.useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="tarif-arsip">
      <div data-testid="tarif-berlaku" className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
        <div className="text-[15px] font-bold text-soq-biru-tua">Arsip — tarif lama (satu tarif untuk semua barang)</div>
        <p className="mt-1 text-sm text-slate-300">Hanya-baca. Dipakai untuk SPK yang dibuat sebelum tarif per jenis jahitan. Tarif baru diatur di tab <b>Tarif per jenis jahitan</b>.</p>
        <div className="mt-2 text-sm text-slate-400">Tarif terakhir: <span className="font-mono font-semibold text-slate-200">{d?.berlaku ? rupiah(d.berlaku.nominal) : "tidak ada"}</span>
          {d?.berlaku && <> · berlaku mulai {tanggalIndo(d.berlaku.berlaku_mulai)}</>}</div>
        {msg && <p className="mt-1 text-sm text-red-300">{msg}</p>}
      </div>
      <div>
        <div className="text-[13px] font-semibold text-slate-500 mb-1">Riwayat tarif lama</div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold text-slate-500"><th className="px-2">Berlaku mulai</th><th className="px-2 text-right">Per pcs</th><th className="px-2">Diisi</th><th className="px-2">Catatan</th></tr></thead>
          <tbody>
            {!d?.riwayat?.length && <tr><td colSpan={4} className="px-2 py-3 text-slate-500">Tidak ada tarif lama.</td></tr>}
            {d?.riwayat?.map((t) => (
              <tr key={t.id} className={`border-t border-slate-800 ${t.aktif ? "" : "text-slate-500 line-through"}`}>
                <td className="px-2 py-1.5">{tanggalIndo(t.berlaku_mulai)}{d.berlaku?.id === t.id && <span className="ml-2 text-xs text-emerald-300">terakhir</span>}</td>
                <td className="px-2 py-1.5 text-right font-mono">{rupiah(t.nominal)}</td>
                <td className="px-2 py-1.5 text-xs text-slate-400">{t.oleh?.username} {t.dibuat ? W.tanggal(t.dibuat) : ""}</td>
                <td className="px-2 py-1.5 text-xs text-slate-400">{t.catatan}{t.nonaktif ? ` · nonaktif: ${t.nonaktif.alasan}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Penyesuaian({ minggu, onSaved }) {
  const [users, setUsers] = React.useState([]);
  const [f, setF] = React.useState({ operator_id: "", nilai: "", alasan: "" });
  const [msg, setMsg] = React.useState(null);
  React.useEffect(() => { apiListUsers().then((xs) => setUsers(xs.filter((u) => !["admin", "kasir", "finance"].includes(u.role)))).catch(() => {}); }, []);
  const simpan = async (e) => {
    e.preventDefault(); setMsg(null);
    try { await apiBoronganPenyesuaian({ ...f, nilai: parseInt(f.nilai, 10), minggu }); setMsg("Penyesuaian tercatat"); setF({ operator_id: "", nilai: "", alasan: "" }); onSaved(); }
    catch (e2) { setMsg(errText(e2)); }
  };
  return (
    <form data-testid="borongan-penyesuaian" onSubmit={simpan} className="flex flex-wrap items-end gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
      <span className="w-full text-xs text-slate-400">Penyesuaian Owner (bonus / potongan lain / koreksi minggu yang sudah dibayar). Angka minus = potongan.</span>
      <select required value={f.operator_id} onChange={(e) => setF({ ...f, operator_id: e.target.value })} className={inputCls}>
        <option value="">— operator —</option>{users.map((u) => <option key={u.id} value={u.id}>{u.nama} ({u.username})</option>)}</select>
      <input required type="number" step="1" placeholder="Nilai (Rp)" value={f.nilai} onChange={(e) => setF({ ...f, nilai: e.target.value })} className={`${inputCls} w-36`} />
      <input required minLength={5} placeholder="Alasan" value={f.alasan} onChange={(e) => setF({ ...f, alasan: e.target.value })} className={`${inputCls} flex-1 min-w-[12rem]`} />
      <button className="rounded-lg border border-amber-500/50 px-3 py-2 text-sm text-amber-300">Catat</button>
      {msg && <span className="w-full text-xs text-slate-300">{msg}</span>}
    </form>
  );
}

function Rekap({ isAdmin }) {
  const [minggu, setMinggu] = React.useState(null);
  const [d, setD] = React.useState(null);
  const [akun, setAkun] = React.useState([]);
  const [akunPilih, setAkunPilih] = React.useState("TUNAI");
  const [buka, setBuka] = React.useState(null);
  const [entri, setEntri] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [msg, setMsg] = React.useState(null);
  const [cek, setCek] = React.useState(null);
  const load = React.useCallback(() => apiBoronganRekap(minggu).then((x) => { setD(x); setMsg(null); }).catch((e) => setMsg(errText(e))), [minggu]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { apiBukuKas({}).then((x) => setAkun(x.saldo_per_akun || [])).catch(() => {}); }, []);
  React.useEffect(() => { if (isAdmin) apiBoronganCek(false).then(setCek).catch(() => {}); }, [isAdmin]);
  React.useEffect(() => { setBuka(null); }, [minggu]);
  const lihat = async (opId) => {
    if (buka === opId) { setBuka(null); return; }
    setBuka(opId); setEntri(null);
    try { setEntri(await apiBoronganEntri(d.minggu, opId)); } catch (e) { setMsg(errText(e)); }
  };
  const aksi = async (key, fn, ok) => {
    setBusy(key); setMsg(null);
    try { const r = await fn(); if (r?.minggu && r?.operator) setD(r); else await load(); setMsg(ok(r)); } catch (e) { setMsg(errText(e)); } finally { setBusy(null); }
  };
  if (!d) return msg ? <div className="text-sm text-red-300">{msg}</div> : <Loader2 size={16} className="animate-spin text-slate-400" />;
  const terkunci = d.status !== "TERBUKA";
  const belumBayar = d.operator.filter((r) => r.total > 0 && !r.dibayar);
  return (
    <div className="space-y-4">
      {cek?.hilang?.length > 0 && (
        <div data-testid="borongan-cek" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200 flex flex-wrap items-center gap-3">
          <AlertTriangle size={15} /> {cek.hilang.length} pekerjaan selesai belum tercatat di borongan.
          <button onClick={() => aksi("cek", () => apiBoronganCek(true), (r) => `${r.dibuat} catatan dibuat`).then(() => apiBoronganCek(false).then(setCek))} className="rounded border border-red-400/50 px-2 py-1 text-xs">Perbaiki</button>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <MingguNav minggu={d.minggu} sampai={d.sampai} d={d} onChange={setMinggu} />
        <span data-testid="borongan-status" className={`rounded px-2 py-0.5 text-xs font-mono ${d.status === "DIBAYAR" ? "bg-emerald-500/20 text-emerald-300" : d.status === "DIKUNCI" ? "bg-sky-500/20 text-sky-300" : "bg-slate-700 text-slate-300"}`}>{d.status}</span>
        {d.dikunci && <span className="text-xs text-slate-400">dikunci {d.dikunci.username} {W.waktu(d.dikunci.waktu)}</span>}
        <button onClick={() => window.print()} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-400"><Printer size={13} /> Cetak</button>
      </div>
      <div className="overflow-x-auto print:overflow-visible">{/* B-15 */}
      <table data-testid="borongan-rekap" className="w-full text-sm">
        <thead><tr className="text-left text-xs font-semibold text-slate-500">
          <th className="px-2 py-1">Operator</th><th className="px-2 py-1 text-right">Saldo bawaan</th><th className="px-2 py-1 text-right">Hasil</th><th className="px-2 py-1 text-right">Potongan</th>
          <th className="px-2 py-1 text-right">Penyesuaian</th><th className="px-2 py-1 text-right">Total</th><th className="px-2 py-1">Status</th></tr></thead>
        <tbody>
          {d.operator.length === 0 && <tr><td colSpan={7} className="px-2 py-3 text-slate-500">Belum ada catatan borongan minggu ini.</td></tr>}
          {d.operator.map((r) => (
            <React.Fragment key={r.operator.id}>
              <tr data-testid={`borongan-op-${r.operator.username}`} onClick={() => lihat(r.operator.id)} className="border-t border-slate-800 cursor-pointer hover:bg-slate-900">
                <td className="px-2 py-1.5">{r.operator.nama || r.operator.username}<span className="block text-[11px] text-slate-500">{r.jumlah_entri} catatan
                  {r.tanpa_tarif > 0 && <span className="text-amber-300"> · {r.tanpa_tarif} tanpa tarif</span>}{r.perlu_ditinjau > 0 && <span className="text-amber-300"> · {r.perlu_ditinjau} potongan perlu ditinjau</span>}</span></td>
                <td className="px-2 py-1.5 text-right font-mono text-red-300">{r.saldo_bawaan ? rupiah(r.saldo_bawaan) : "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{rupiah(r.hasil)}</td>
                <td className="px-2 py-1.5 text-right font-mono text-red-300">{r.potongan ? `-${rupiah(r.potongan)}` : "—"}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.penyesuaian ? rupiah(r.penyesuaian) : "—"}</td>
                <td className={`px-2 py-1.5 text-right font-mono font-semibold ${r.total < 0 ? "text-red-300" : ""}`}>{rupiah(r.total)}</td>
                <td className="px-2 py-1.5 text-xs">{r.dibayar ? <span className="text-emerald-300">dibayar {r.dibayar.akun?.nama}</span> : r.total <= 0 && terkunci ? <span className="text-slate-500">tidak dibayar</span> : "—"}</td>
              </tr>
              {buka === r.operator.id && <tr><td colSpan={7} className="px-2 pb-3"><DaftarEntri rows={entri} isAdmin={isAdmin} terkunci={terkunci} onDone={() => { load(); apiBoronganEntri(d.minggu, r.operator.id).then(setEntri).catch((e) => setMsg(errText(e))); }} /></td></tr>}
            </React.Fragment>
          ))}
          {d.operator.length > 0 && <tr className="border-t-2 border-slate-700"><td className="px-2 py-1.5 font-semibold">Total</td><td colSpan={4}></td><td className="px-2 py-1.5 text-right font-mono font-semibold">{rupiah(d.total)}</td><td></td></tr>}
        </tbody>
      </table>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {!terkunci && isAdmin && <button onClick={() => aksi("hitung", () => apiBoronganHitungUlang(d.minggu), (r) => `Hitung ulang: ${r.dibuat} catatan susulan`)} className="inline-flex items-center gap-1 rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-200"><RefreshCw size={14} /> Hitung ulang tarif</button>}
        {!terkunci && <button data-testid="borongan-kunci" disabled={!d.bisa_dikunci || busy} title={d.bisa_dikunci ? "" : "Bisa dikunci setelah hari Minggu minggu ini lewat"}
          onClick={() => window.confirm("Kunci rekap minggu ini? Setelah dikunci angka tidak berubah lagi.") && aksi("kunci", () => apiBoronganKunci(d.minggu), () => "Periode dikunci")}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-40"><Lock size={14} /> Kunci periode</button>}
        {d.status === "DIKUNCI" && belumBayar.length > 0 && (
          <>
            <select data-testid="borongan-akun" value={akunPilih} onChange={(e) => setAkunPilih(e.target.value)} className={inputCls}>
              {akun.filter((a) => a.aktif !== false).map((a) => <option key={a.kode} value={a.kode}>{a.nama}</option>)}</select>
            <button data-testid="borongan-bayar" disabled={!!busy}
              onClick={() => window.confirm(`Bayar ${belumBayar.length} operator, total ${rupiah(belumBayar.reduce((s, r) => s + r.total, 0))}? Masuk buku kas sebagai pengeluaran.`) && aksi("bayar", () => apiBoronganBayar(d.minggu, { akun: akunPilih }), (r) => `Dibayar: ${r.baru_dibayar.join(", ") || "—"}`)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-40">
              {busy === "bayar" ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />} Bayar semua yang belum</button>
          </>
        )}
        {msg && <span data-testid="borongan-msg" className="text-sm text-slate-300">{msg}</span>}
      </div>
      {isAdmin && !terkunci && <Penyesuaian minggu={d.minggu} onSaved={load} />}
      <p className="text-xs text-slate-500">Total minus tidak dibayar dan otomatis dibawa ke minggu berikutnya sebagai saldo bawaan (minggu dikunci berurutan). Koreksi minggu yang sudah dibayar = penyesuaian di minggu yang belum dikunci.</p>
    </div>
  );
}

export default function BoronganPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const bolehRekap = isAdmin || user?.role === "finance";
  const [tab, setTab] = React.useState(bolehRekap ? "rekap" : "saya");
  const tabs = [...(bolehRekap ? [["rekap", "Rekap mingguan"], ["jahit", "Tarif per jenis jahitan"]] : []),
    ...(isAdmin ? [["tarif", "Arsip"]] : []), ...(!bolehRekap ? [["saya", "Borongan saya"]] : [])];
  return (
    <div data-testid="borongan-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Gaji Borongan</h1>
          <p className="text-sm text-slate-400 mt-1">Hanya penjahit (pos Cutting &amp; Jahit): dihitung otomatis dari pcs hasil × tarif. Reject QC dengan pos penyebab Cutting &amp; Jahit dipotong. Satu minggu = 7 hari mulai hari yang diatur Owner di Pengaturan (bawaan Senin–Minggu); minggu minus dibawa ke minggu berikutnya.</p>
        </div>
        {tabs.length > 1 && (
          <div className="flex gap-2">
            {tabs.map(([k, l]) => <button key={k} data-testid={`borongan-tab-${k}`} onClick={() => setTab(k)} className={`rounded-md px-3 py-1.5 text-sm border ${tab === k ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`}>{l}</button>)}
          </div>
        )}
        {tab === "rekap" && bolehRekap && <Rekap isAdmin={isAdmin} />}
        {tab === "jahit" && bolehRekap && <TarifJahitPanel isAdmin={isAdmin} />}
        {tab === "tarif" && isAdmin && <Tarif />}
        {tab === "saya" && <Saya />}
      </main>
    </div>
  );
}
